import styled from '@emotion/styled';
import {
    Heading2,
    Text,
    CheckBox,
    Button,
    Modal,
    ButtonWithIcon,
    FieldMessage
} from '@blueprint/ui';
import { css } from '@emotion/react';
import { getRems } from '@blueprint/utils';
import { AuthModalProps } from './AuthModal.types';

const StyledModal = styled(Modal)<Partial<AuthModalProps>>(
    (props) => css`
        ${props.isSignInModalEnabled &&
        `${props.theme.mediaQueries.small} {
            min-height: 90vh;
        }`}
    `
);

const AuthModalHeading = styled(Heading2)(
    (props) => css`
        text-align: center;
        margin: 0 ${props.theme.spacing['24px']} ${props.theme.spacing['48px']}
            ${props.theme.spacing['24px']};
        ${props.theme.mq.small} {
            margin: 0 ${props.theme.spacing['80px']}
                ${props.theme.spacing['24px']} ${props.theme.spacing['80px']};
        }
    `
);

const AuthModalSubHeadingWrapper = styled('div')`
    display: flex;
    justify-content: space-between;
    align-items: center;
`;

const RequiredIndicator = styled('span')((props) => {
    return css`
        color: ${props.theme.colors['function-error']};
    `;
});

const AuthContextText = styled(Text)(
    (props) => css`
        text-align: center;
        margin: ${props.theme.spacing['24px']} ${props.theme.spacing['48px']};
        ${props.theme.mq.small} {
            margin: ${props.theme.spacing['24px']}
                ${props.theme.spacing['80px']};
        }

        > a {
            margin-left: ${props.theme.spacing['8px']};
        }
    `
);

const FormSection = styled('div')<Partial<AuthModalProps>>(
    (props) => css`
        margin-bottom: ${props.theme.spacing['32px']};

        &:first-of-type {
            margin-top: ${props.isSignInModalEnabled
                ? props.theme.spacing['24px']
                : props.theme.spacing['32px']};
        }

        > *:not(:last-child) {
            margin-bottom: ${props.theme.spacing['16px']};
        }
    `
);

const AboutYouLabel = styled('label')(
    (props) => css`
        font-size: ${props.theme.type.secondary};
        line-height: ${props.theme.type.secondary};
        color: ${props.theme.colors['text-body']};
        display: block;
        margin-bottom: 0;
    `
);

const AuthModalCheckbox = styled(CheckBox)(
    (props) => css`
        margin-top: ${props.theme.spacing['16px']};
        margin-bottom: ${props.theme.spacing['16px']};
    `
);

const ReassuranceText = styled(Text)(
    (props) => css`
        margin-bottom: ${props.theme.spacing['32px']};
        text-align: center;

        ${props.theme.mediaQueries.medium} {
            text-align: left;
        }
    `
);

const TermsText = styled(Text)(`
        text-align: center;
`);

const RegisterButton = styled(Button)(
    (props) => css`
        margin-bottom: ${props.theme.spacing['48px']};

        ${props.theme.mediaQueries.large} {
            margin-bottom: ${props.theme.spacing['32px']};
        }
    `
);

const SignInButton = styled(Button)(
    (props) => css`
        margin-bottom: ${props.theme.spacing['24px']};
    `
);

const SocialSignInWrapper = styled('div')(
    (props) => css`
        margin-bottom: ${props.theme.spacing['16px']};

        ${ButtonWithIcon} {
            position: relative;
            color: ${props.theme.colors['neutral-6']};
            border-color: ${props.theme.colors['neutral-4']};

            /** fixed height for button with bigger icons, fix for Safari (Mac) **/
            height: ${getRems(44.15)};
            padding-top: 0;
            padding-bottom: 0;

            &:before {
                content: '';
                position: absolute;
                top: 0;
                left: 0;
                right: 0;
                bottom: 0;
                display: block;
                width: 100%;
                height: 100%;
                border-radius: inherit;
            }

            &:hover,
            &:focus {
                color: ${props.theme.colors['neutral-6']};
                border-color: ${props.theme.colors['neutral-4']};
                background-color: ${props.theme.colors['white']};
            }

            &:hover {
                &:before {
                    box-shadow: 0 0 0 ${getRems(2)}
                        ${props.theme.colors['neutral-4']};
                }
            }
        }

        ${ButtonWithIcon} + ${ButtonWithIcon} {
            margin-top: ${props.theme.spacing['24px']};
        }
    `
);

const SocialSignInText = styled(Text)(
    (props) => css`
        text-align: center;
        margin-bottom: ${props.theme.spacing['24px']};
    `
);

const SignInErrorMessage = styled(FieldMessage)(
    (props) => css`
        line-height: ${props.theme.type.lineHeight};
    `
);

export {
    StyledModal,
    AuthModalHeading,
    AuthModalSubHeadingWrapper,
    RequiredIndicator,
    AuthContextText,
    FormSection,
    AboutYouLabel,
    AuthModalCheckbox,
    ReassuranceText,
    TermsText,
    RegisterButton,
    SocialSignInWrapper,
    SocialSignInText,
    SignInButton,
    SignInErrorMessage
};
